import type Alpine from 'alpinejs';
import {
    findAlpineMachineOwner,
    type AlpineMachineOwner,
} from '@client/runtime/alpine-machine';
import type {TransitionContext} from '@client/runtime/transition-coordinator';
import {warn} from '@client/utils';

type TransitionEvaluator = ReturnType<Alpine.DirectiveUtilities['evaluateLater']>;

const evaluateTransition = <TProps, TData, TElement>(
    evaluator: TransitionEvaluator,
    transition: TransitionContext<TProps, TData, TElement>,
): unknown => {
    let synchronous = true;
    let completedSynchronously = false;
    let result: unknown;
    let resolve!: (value: unknown) => void;
    const pending = new Promise<unknown>((resolvePromise) => {
        resolve = resolvePromise;
    });

    evaluator((value) => {
        if (synchronous) {
            completedSynchronously = true;
            result = value;
            return;
        }
        resolve(value);
    }, {scope: {$transition: transition}});
    synchronous = false;

    return completedSynchronously ? result : pending;
};

const registerTransitionDirective = (AlpineInstance: Alpine.Alpine): void => {
    AlpineInstance.directive(
        'ui-transition',
        AlpineInstance.skipDuringClone((el, {value: name, expression}, utilities) => {
            if (!name) {
                warn('x-ui-transition requires a transition name', el);
                return;
            }

            const owner = findAlpineMachineOwner(AlpineInstance, el);
            if (!owner) {
                warn(`x-ui-transition:${name} requires an owning Trellis machine`, el);
                return;
            }

            const evaluator = expression?.trim() ? utilities.evaluateLater(expression) : null;
            const unregister = owner.onTransition(name, (transition) => (
                evaluator ? evaluateTransition(evaluator, transition) : undefined
            ), el as Parameters<AlpineMachineOwner['onTransition']>[2]);
            utilities.cleanup(unregister);
        }),
    );
};

export {registerTransitionDirective};
